import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { CalendarClock, CheckCircle, Clock, Euro, Printer, RefreshCw, Trash2, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatDate, formatMoney } from './constants';

export default function EcheanciersTab({
  expandedIds,
  filtered,
  getName,
  handleDeleteEcheancier,
  handlePrintEcheancier,
  handleResetEcheancier,
  toggleExpand
}) {
  const withEcheancier = filtered.filter(d => d.echeancier_personnalise?.echeances?.length > 0);

  return (
    <>
      {/* KPIs compactes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <CalendarClock className="h-4 w-4 text-violet-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Avec echeancier</p>
            <p className="text-lg font-bold text-gray-900">{withEcheancier.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Payees</p>
            <p className="text-lg font-bold text-emerald-700">
              {filtered.reduce((sum, d) => sum + (d.echeancier_personnalise?.echeances?.filter(e => e.statut === 'paye').length || 0), 0)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <Clock className="h-4 w-4 text-amber-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">En attente</p>
            <p className="text-lg font-bold text-amber-700">
              {filtered.reduce((sum, d) => sum + (d.echeancier_personnalise?.echeances?.filter(e => e.statut !== 'paye').length || 0), 0)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <Euro className="h-4 w-4 text-rose-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total</p>
            <p className="text-lg font-bold text-rose-700">
              {formatMoney(filtered.reduce((sum, d) => sum + (d.echeancier_personnalise?.echeances?.reduce((s, e) => s + (e.montant || 0), 0) || 0), 0))} EUR
            </p>
          </div>
        </div>
      </div>

      {/* Count */}
      <div className="flex items-center gap-3 px-1">
        <span className="text-sm text-gray-500">{withEcheancier.length} echeancier(s)</span>
      </div>

      {/* Liste */}
      {withEcheancier.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <CalendarClock className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 font-medium">Aucun echeancier configure</p>
          <p className="text-sm text-gray-400">Cliquez sur "Echeancier" sur une souscription pour creer un plan de paiement</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="echeanciers-list">
          {withEcheancier.map((doc, idx) => {
            const echeances = doc.echeancier_personnalise?.echeances || [];
            const paidCount = echeances.filter(e => e.statut === 'paye').length;
            const totalAmount = echeances.reduce((s, e) => s + (e.montant || 0), 0);
            const paidAmount = echeances.filter(e => e.statut === 'paye').reduce((s, e) => s + (e.montant || 0), 0);
            const progress = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
            const echId = `ech-${doc.reference}`;
            const isExpanded = expandedIds.has(echId);

            return (
              <div
                key={echId}
                className={cn(
                  "rounded-lg border transition-all duration-150",
                  isExpanded ? "border-violet-200 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                )}
                data-testid={`echeancier-row-${idx}`}
              >
                {/* Compact row */}
                <button onClick={() => toggleExpand(echId)} className="flex items-center gap-3 px-4 py-2.5 w-full text-left" data-testid={`echeancier-toggle-${idx}`}>
                  <CalendarClock className={cn("h-4 w-4 shrink-0", progress >= 100 ? "text-emerald-500" : "text-violet-500")} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-medium text-sm truncate", isExpanded ? "text-violet-700" : "text-gray-900")}>{getName(doc)}</p>
                    <p className="text-[10px] text-gray-400 font-mono truncate">{doc.reference}</p>
                  </div>
                  <Badge className={cn("text-[10px] shrink-0", progress >= 100 ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700")}>
                    {paidCount}/{echeances.length}
                  </Badge>
                  <div className="hidden sm:flex items-center gap-2 w-24 shrink-0">
                    <div className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="text-[10px] text-gray-500 tabular-nums">{progress.toFixed(0)}%</span>
                  </div>
                  <span className="text-sm font-bold text-violet-700 tabular-nums shrink-0">{formatMoney(totalAmount)} EUR</span>
                  <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-violet-500")} />
                </button>

                {/* Expanded */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`echeancier-details-${idx}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Souscripteur</h4>
                        <div className="space-y-1.5 text-sm">
                          <p className="font-medium text-gray-900">{getName(doc)}</p>
                          <p className="text-gray-500 text-xs font-mono">{doc.reference}</p>
                          <div className="flex justify-between text-xs mt-2">
                            <span className="text-gray-500">Paye:</span>
                            <span className="font-bold text-emerald-700">{formatMoney(paidAmount)} EUR</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Reste:</span>
                            <span className="font-bold text-red-600">{formatMoney(totalAmount - paidAmount)} EUR</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Echeances</h4>
                        <div className="flex gap-1.5 overflow-x-auto pb-1">
                          {echeances.map((ech, i) => (
                            <div key={i} className={cn(
                              "flex-shrink-0 px-3 py-1.5 rounded-md border text-xs",
                              ech.statut === 'paye' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'
                            )}>
                              <p className="font-semibold">Ech. {i + 1}</p>
                              <p className="font-bold">{formatMoney(ech.montant)}</p>
                              <p className="text-[10px] opacity-70">{formatDate(ech.date_prevue)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
                        <div className="flex flex-wrap gap-1.5">
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50" data-testid={`echeancier-print-${idx}`}
                            onClick={(e) => { e.stopPropagation(); handlePrintEcheancier(doc); }}
                          ><Printer className="h-3.5 w-3.5" /> Imprimer</Button>
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid={`echeancier-reset-${idx}`}
                            onClick={(e) => { e.stopPropagation(); handleResetEcheancier(doc); }} disabled={paidCount === 0}
                          ><RefreshCw className="h-3.5 w-3.5" /> Reinitialiser</Button>
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-red-200 text-red-700 hover:bg-red-50" data-testid={`echeancier-delete-${idx}`}
                            onClick={(e) => { e.stopPropagation(); handleDeleteEcheancier(doc); }}
                          ><Trash2 className="h-3.5 w-3.5" /> Supprimer</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
